// INDEX_SPLIT_B2_v1 (07.09.2026) -- Kunden: CustomersPage
// 1:1 aus app.jsx verschoben (Reihenfolge unverändert), nur `export` ergänzt und Imports erzeugt (scripts/split_frontend.mjs).
import { AlertTriangle, Calendar, Check, CheckCircle, Edit3, Mail, MessageSquare, Phone, Plus, Search, Shield, Trash2, UserPlus, X, useEffect, useState } from "../core/vendor.js";
import { BookingModal } from "../calendar/calendar.jsx";
import { api } from "../core/api.js";
import { COLORS, HAIR_CONDITIONS, HAIR_LENGTHS, HAIR_THICKNESS, HAIR_TYPES, findDuplicateCustomers, formatDate, formatDateShort, today } from "../core/config.jsx";
import { Badge, Btn, Card, DateDMYInput, DupCustomerHint, Input, Modal, Select } from "../core/ui.jsx";
import { Header } from "../layout/layout.jsx";

// CUST_BIRTHDAY_TYPE_v1 (Toby 29.09.2026, Meldung Hamza) -- Geburtstag als Tippfeld TT.MM.JJJJ statt
// Handy-Kalender (am Android war das Geburtsjahr kaum zu finden). 8 Ziffern tippen, die Punkte kommen
// von selbst; gespeichert wird weiter JJJJ-MM-TT. Aussehen wie Input (Label + Feld).
const BirthdayField = ({ value, onChange }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>Geburtstag</label>
    <DateDMYInput value={value} onChange={onChange} placeholder="TT.MM.JJJJ (z. B. 14.05.1990)"
      style={{ width: "100%", padding: "8px 12px", border: `1px solid ${COLORS.border}`, borderRadius: 8,
               fontSize: 14, outline: "none", boxSizing: "border-box" }} />
  </div>
);


// ============================================================
// PAGE: CRM / Customers (erweitert)
// ============================================================
export const CustomersPage = ({ customers, setCustomers, appointments, employees, services, setAppointments, currentUser, enabledFeatures, focusCustomerId, onFocusConsumed }) => {
  const [search, setSearch] = useState("");
  // CUST_SORT_LASTNAME_v1 (Tamara 21.09.2026) -- Sortierung Vorname/Nachname, pro Geraet gemerkt.
  // Name ist EIN Feld ("Vorname Nachname") -> Nachname = letztes Wort.
  const [sortMode, setSortMode] = useState(() => { try { return localStorage.getItem("sp_cust_sort") === "last" ? "last" : "first"; } catch (_) { return "first"; } });
  const changeSort = (m) => { setSortMode(m); try { localStorage.setItem("sp_cust_sort", m); } catch (_) {} };
  const [selected, setSelected] = useState(null);
  // SPOTLIGHT_CRM_JUMP_v1 -- Sprung aus der Cmd+K-Suche: Kunde öffnen, Zeile kurz
  // hervorheben und in Sicht scrollen. focusCustomerId wird nach Verbrauch genullt.
  const [highlightId, setHighlightId] = useState(null);
  useEffect(() => {
    if (!focusCustomerId) return;
    const cust = (customers || []).find(c => c.id === focusCustomerId);
    if (cust) {
      setSelected(cust);
      setCrmTab("info");
      setSearch("");
      setFilterType("all");
      setHighlightId(cust.id);
      setTimeout(() => {
        const row = document.getElementById("crm-row-" + cust.id);
        if (row && row.scrollIntoView) row.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 150);
      setTimeout(() => setHighlightId(null), 4000);
    }
    if (onFocusConsumed) onFocusConsumed();
  }, [focusCustomerId]);
  // CUSTOMER_EDIT_v1 -- inline-Bearbeiten fuer Kundendaten
  const [editCust, setEditCust] = useState(null); // null oder {form-state mit allen Feldern}
  const [showBooking, setShowBooking] = useState(false);
  const [bookingCustomerId, setBookingCustomerId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [crmTab, setCrmTab] = useState("info");
  const [newCardEntry, setNewCardEntry] = useState("");
  // LOYALTY_ENGINE_v1 -- Einloesen/Korrektur-Formular im Kundenprofil
  const [loyaltyForm, setLoyaltyForm] = useState(null); // null oder {points:"", note:""}
  const [loyaltyBusy, setLoyaltyBusy] = useState(false);
  const [loyaltyErr, setLoyaltyErr] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [newCust, setNewCust] = useState({ name: "", phone: "", email: "", notes: "", address: "", hairType: "", hairLength: "", hairThickness: "", hairCondition: "", birthday: "", customerType: "laufkunde", comesRegularly: false, regularProducts: [], cardHistory: [] });
  // BULK_CONSENT_v1 -- Sammel-Aktion: Einwilligungen fuer alle Kunden setzen
  const [showBulkConsent, setShowBulkConsent] = useState(false);
  const [bulkSms, setBulkSms] = useState(true);
  const [bulkMkt, setBulkMkt] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState("");
  const [bulkAttest, setBulkAttest] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  // BULK_CONSENT_LOCK_v1 -- Lock-State (gesperrt nach erster Bestaetigung)
  const [bulkLock, setBulkLock] = useState(null); // null=loading, {locked: bool, confirmed_at?: iso}
  useEffect(() => {
    if (!showBulkConsent) return;
    setBulkLock(null);
    api.request("/customers/bulk-consent/status").then(r => {
      if (r && r.ok) setBulkLock(r.data);
      else setBulkLock({ locked: false });
    }).catch(() => setBulkLock({ locked: false }));
  }, [showBulkConsent]);

  const filtered = customers.filter(c => {
    // CRM_SEARCH_NULLSAFE_v1 -- defensive String-Coercion. Bei importierten Kunden ohne Telefon
    // (z.B. Natalies 1393 Wolf-Kunden) wirft c.phone.includes(...) einen TypeError → React-Crash → Whitescreen.
    // CRM_SEARCH_BROADER_v1 -- auch notes durchsuchen damit man Karteileichen aus Tests findet ("Test", "Tobias").
    const _name = String(c.name || "").toLowerCase();
    const _phone = String(c.phone || "");
    // Phone-Variante ohne Leerzeichen/Bindestriche/Plus für robusteren Match
    const _phoneRaw = _phone.replace(/[^\d]/g, "");
    const _email = String(c.email || "").toLowerCase();
    const _notes = String(c.notes || "").toLowerCase();
    const _q = String(search || "").toLowerCase();
    const _qRaw = _q.replace(/[^\d]/g, "");
    const matchSearch = !_q
      || _name.includes(_q)
      || _phone.includes(search)
      || (_qRaw.length >= 4 && _phoneRaw.includes(_qRaw))
      || _email.includes(_q)
      || _notes.includes(_q);
    const matchType = filterType === "all" || c.customerType === filterType || (filterType === "regular" && c.comesRegularly);
    return matchSearch && matchType;
  });
  {
    const _full = c => String(c.name || "").trim();
    const _last = c => { const w = _full(c).replace(/\([^)]*\)/g, " ").trim().split(/\s+/); return w[w.length - 1] || ""; }; // Klammerzusatz ignorieren
    const _cmp = (a, b) => a.localeCompare(b, "de", { sensitivity: "base" });
    filtered.sort(sortMode === "last"
      ? (a, b) => _cmp(_last(a), _last(b)) || _cmp(_full(a), _full(b))
      : (a, b) => _cmp(_full(a), _full(b)));
  }

  // NEW_CUST_QUICK_BOOK_v1 -- bookAfter: nach Speichern direkt BookingModal mit
  // diesem Kunden vorausgewählt. Use-Case: Kunde steht an der Kasse, will gleich
  // Folgetermin ausmachen.
  const addCustomer = async (bookAfter = false) => {
      try {
          const res = await api.createCustomer({
              name: newCust.name,
              phone: newCust.phone,
              email: newCust.email || "",
              notes: newCust.notes || "",
              address: newCust.address || "",
              customer_type: newCust.customerType || "laufkunde",
              hair_type: newCust.hairType || "",
              hair_length: newCust.hairLength || "",
              hair_thickness: newCust.hairThickness || "",
              hair_condition: newCust.hairCondition || "",
              birthday: newCust.birthday || "",
              comes_regularly: newCust.comesRegularly ? 1 : 0,
              regular_products: newCust.regularProducts || [],
                    sms_consent: newCust.smsConsent ? 1 : 0,
          });
          if (res.ok) {
              const data = res.data || res;
              const newId = data.id || Math.max(0, ...customers.map(c => c.id)) + 1;
              setCustomers(prev => [...prev, { ...newCust, id: newId, lastVisit: "", totalSpent: 0, visits: 0, preferredEmployee: null, noShows: 0 }]);
              setShowAdd(false);
              setNewCust({ name: "", phone: "", email: "", notes: "", address: "", hairType: "", hairLength: "", hairThickness: "", hairCondition: "", birthday: "", customerType: "laufkunde", comesRegularly: false, smsConsent: true, regularProducts: [], cardHistory: [] });
              if (bookAfter) {
                  setBookingCustomerId(newId);
                  setShowBooking(true);
              }
          } else {
              // APPT_SAVE_ERRMSG_FIX_v1 -- Fehlertext liegt in res.data.error (api.request-Form);
              // res.error gab es nie -> zeigte immer nur "Unbekannter Fehler"
              alert("Fehler beim Speichern: " + ((res.data && res.data.error) || "Unbekannter Fehler"));
          }
      } catch (e) {
          console.error("Kunde speichern fehlgeschlagen:", e);
          alert("Kunde konnte nicht gespeichert werden.");
      }
  };

  const addCardEntry = () => {
    if (!newCardEntry.trim() || !selected) return;
    const entry = { date: formatDate(today), employee: "Aktueller Benutzer", text: newCardEntry };
    setCustomers(prev => prev.map(c => c.id === selected.id ? { ...c, cardHistory: [entry, ...(c.cardHistory || [])] } : c));
    setSelected(s => s ? { ...s, cardHistory: [entry, ...(s.cardHistory || [])] } : s);
    setNewCardEntry("");
  };

  return (
    <div>
      <Header title="Kunden (CRM)" subtitle={`${customers.length} Kunden im System`} />
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ flex: 1, position: "relative", minWidth: 200 }}>
          <Search size={16} style={{ position: "absolute", left: 12, top: 10, color: COLORS.textLight }} />
          <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Kunde suchen (Name oder Telefon)..."
            /* CUSTSEARCH_NOAUTOFILL_v1 -- Browser-Autocomplete + Keychain abschalten */
            autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false"
            name="shearly-customer-search" data-1p-ignore data-lpignore="true" data-form-type="other"
            style={{ width: "100%", padding: "8px 12px 8px 36px", border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14, outline: "none", boxSizing: "border-box" }} />
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {[
            { val: "all", label: "Alle" },
            { val: "stammkunde", label: "Stammkunden" },
            { val: "laufkunde", label: "Laufkunden" },
            { val: "regular", label: "Kommt regelmäßig" },
          ].map(f => (
            <Btn key={f.val} variant={filterType === f.val ? "primary" : "secondary"} size="sm" onClick={() => setFilterType(f.val)}>{f.label}</Btn>
          ))}
        </div>
        {/* CUST_SORT_LASTNAME_v1 */}
        <div style={{ display: "flex", gap: 4, alignItems: "center" }} title="Sortierung der Kundenliste">
          <span style={{ fontSize: 12, color: COLORS.textLight, marginRight: 2 }}>A–Z:</span>
          {[{ val: "first", label: "Vorname" }, { val: "last", label: "Nachname" }].map(o => (
            <Btn key={o.val} variant={sortMode === o.val ? "primary" : "secondary"} size="sm" onClick={() => changeSort(o.val)}>{o.label}</Btn>
          ))}
        </div>
        <Btn onClick={() => setShowAdd(true)}><UserPlus size={16} /> Neuer Kunde</Btn>
        {/* BULK_CONSENT_v1 -- Einwilligungen fuer alle Kunden setzen */}
        <Btn variant="secondary" onClick={() => { setShowBulkConsent(true); setBulkResult(null); setBulkConfirm(""); setBulkAttest(false); setBulkSms(true); setBulkMkt(false); }}><Shield size={16} /> Einwilligungen…</Btn>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : (selected ? "1fr 1.3fr" : "1fr"), gap: 16 }}>
        <Card style={{ padding: 0 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                {["Name", "Typ", "E-Mail", "Geschlecht", "Telefon", "Besuche", "Umsatz", "Letzter Besuch"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "10px 12px", color: COLORS.textLight, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} id={"crm-row-" + c.id} onClick={() => { setSelected(c); setCrmTab("info"); }} style={{ borderBottom: `1px solid ${COLORS.border}`, cursor: "pointer", background: highlightId === c.id ? `${COLORS.primary}30` : selected?.id === c.id ? `${COLORS.primary}08` : "transparent", transition: "background 0.6s" }}
                  onMouseEnter={e => { if (selected?.id !== c.id && highlightId !== c.id) e.currentTarget.style.background = "var(--cardHover)"; }}
                  onMouseLeave={e => { if (selected?.id !== c.id && highlightId !== c.id) e.currentTarget.style.background = "transparent"; }}>
                  <td style={{ padding: "10px 12px", fontWeight: 600 }}>
                    {c.name}
                    {c.noShows > 1 && <span style={{ marginLeft: 6, fontSize: 10, color: COLORS.danger }}>({c.noShows} No-Shows)</span>}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <Badge text={c.customerType === "stammkunde" ? "Stamm" : "Lauf"} color={c.customerType === "stammkunde" ? COLORS.primary : COLORS.textLight} />
                    {c.comesRegularly && <Badge text="regelmäßig" color={COLORS.success} />}
                  </td>
                  <td style={{ padding: "10px 12px" }}>{c.email || "—"}</td>
                    <td style={{ padding: "10px 12px" }}>{c.gender === "M" ? "M" : c.gender === "F" ? "W" : c.gender === "K" ? "Kind" : "—"}</td>
                    <td style={{ padding: "10px 12px" }}>{c.phone}</td>
                  <td style={{ padding: "10px 12px" }}>{c.visits}</td>
                  <td style={{ padding: "10px 12px", fontWeight: 600 }}>{c.totalSpent} €</td>
                  <td style={{ padding: "10px 12px" }}>{c.lastVisit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {selected && (
          <Card style={{ padding: 0, position: "sticky", top: 80, maxHeight: "calc(100vh - 100px)", overflowY: "auto" }}>
            {/* Customer Header */}
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{selected.name}</h3>
                  <Badge text={selected.customerType === "stammkunde" ? "Stammkunde" : "Laufkunde"} color={selected.customerType === "stammkunde" ? COLORS.primary : COLORS.textLight} />
                  {selected.comesRegularly && <Badge text="kommt regelmäßig" color={COLORS.success} />}
                </div>
                <p style={{ margin: "2px 0 0", color: COLORS.textLight, fontSize: 12 }}>{selected.email} | {selected.phone}</p>
              </div>
              {/* CUSTOMER_EDIT_v1 -- Bearbeiten-Button */}
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => setEditCust({
                  name: selected.name || "",
                  phone: selected.phone || "",
                  email: selected.email || "",
                  birthday: selected.birthday || "",
                  gender: selected.gender || "",
                  notes: selected.notes || "",
                  address: selected.address || "",
                  hairType: selected.hairType || "",
                  hairLength: selected.hairLength || "",
                  hairThickness: selected.hairThickness || "",
                  hairCondition: selected.hairCondition || "",
                  customerType: selected.customerType || "laufkunde",
                  comesRegularly: !!selected.comesRegularly,
                  preferredEmployee: selected.preferredEmployee || "",
                  smsConsent: !!selected.smsConsent,
                  marketingConsent: !!selected.marketingConsent,
                })} title="Kunde bearbeiten"
                  style={{ background: `${COLORS.primary}10`, border: "none", borderRadius: 6, padding: "6px 10px", cursor: "pointer", color: COLORS.primary, fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                  <Edit3 size={13} /> Bearbeiten
                </button>
                {/* CUSTOMER_DELETE_v1 -- DSGVO + Karteileichen aufraeumen. Backend prueft auf aktive Termine. */}
                <button onClick={async () => {
                  if (!selected) return;
                  if (!window.confirm(`"${selected.name}" wirklich löschen?\n\nVergangene Termine bleiben in der Statistik erhalten (Kunde wird entkoppelt). Wenn noch aktive Termine in der Zukunft sind, schlägt das Löschen fehl.`)) return;
                  const res = await api.request("/customers/" + selected.id, { method: "DELETE" });
                  if (res && res.ok) {
                    setCustomers(prev => prev.filter(c => c.id !== selected.id));
                    setSelected(null);
                  } else {
                    const errMsg = (res && res.data && res.data.error) || "Löschen fehlgeschlagen.";
                    alert(errMsg);
                  }
                }} title="Kunde löschen"
                  style={{ background: `${COLORS.danger}10`, border: "none", borderRadius: 6, padding: "6px 10px", cursor: "pointer", color: COLORS.danger, fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                  <Trash2 size={13} /> Löschen
                </button>
                <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={18} color={COLORS.textLight} /></button>
              </div>
            </div>

            {/* CRM Tabs */}
            <div style={{ display: "flex", borderBottom: `1px solid ${COLORS.border}` }}>
              {[
                { id: "info", label: "Kundendaten" },
                { id: "hair", label: "Haartyp" },
                { id: "card", label: "Kundenkartei" },
                { id: "products", label: "Produkte" },
                { id: "history", label: "Termine" },
              ].map(tab => (
                <button key={tab.id} onClick={() => setCrmTab(tab.id)} style={{
                  padding: "10px 16px", fontSize: 12, fontWeight: crmTab === tab.id ? 700 : 400, background: "none", border: "none",
                  cursor: "pointer", color: crmTab === tab.id ? COLORS.primary : COLORS.textLight,
                  borderBottom: crmTab === tab.id ? `2px solid ${COLORS.primary}` : "2px solid transparent"
                }}>{tab.label}</button>
              ))}
            </div>

            <div style={{ padding: 20 }}>
              {/* Tab: Info */}
              {crmTab === "info" && (
                <div style={{ fontSize: 13, lineHeight: 2.2 }}>
                  <div><strong>Telefon:</strong> {selected.phone}</div>
                  <div><strong>E-Mail:</strong> {selected.email || "—"}</div>
                  <div><strong>Geburtstag:</strong> {selected.birthday || "—"}</div>
                        <div><strong>Geschlecht:</strong> {selected.gender === "M" ? "Männlich" : selected.gender === "F" ? "Weiblich" : selected.gender === "K" ? "Kind" : "–"}</div>
                  <div><strong>Kundentyp:</strong> {selected.customerType === "stammkunde" ? "Stammkunde" : "Laufkunde"}</div>
                  <div><strong>Kommt regelmäßig:</strong> {selected.comesRegularly ? "Ja" : "Nein"}</div>
                  <div><strong>Lieblingsmitarbeiter:</strong> {employees.find(e => e.id === selected.preferredEmployee)?.name || "—"}</div>
                  <div><strong>Besuche:</strong> {selected.visits} | <strong>Umsatz:</strong> {selected.totalSpent} € | <strong>No-Shows:</strong> {selected.noShows}</div>
                  {/* LOYALTY_ENGINE_v1 -- Punktestand + Stufe + Einloesen (nur Owner/Admin) */}
                  {enabledFeatures && enabledFeatures.loyalty_program && (
                    <div style={{ marginTop: 10, padding: "10px 12px", background: "var(--cardHover)", borderRadius: 8, lineHeight: 1.6 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>⭐ Treuepunkte:</span>
                        <span style={{ fontSize: 15, fontWeight: 800 }}>{selected.loyaltyPoints || 0}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                          color: selected.loyaltyTier === "neu" ? COLORS.textLight : "#fff",
                          background: { bronze: "#CD7F32", silber: "#8E9196", gold: "#C9A227", platin: "#7C8DA6" }[selected.loyaltyTier] || "var(--cardHover)",
                          border: selected.loyaltyTier === "neu" ? `1px solid ${COLORS.border}` : "none" }}>
                          {{ bronze: "Bronze", silber: "Silber", gold: "Gold", platin: "Platin" }[selected.loyaltyTier] || "Noch keine Stufe"}
                        </span>
                        {currentUser && (currentUser.role === "owner" || currentUser.role === "admin") && !loyaltyForm && (
                          <button onClick={() => { setLoyaltyForm({ points: "", note: "" }); setLoyaltyErr(""); }}
                            style={{ marginLeft: "auto", background: "none", border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 11, fontWeight: 600, color: COLORS.primary }}>
                            ± Punkte buchen
                          </button>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 2 }}>
                        Punkte gibt es automatisch beim Kassieren. Bronze ab 250 gesammelten Punkten (≈ 3 Besuche).
                      </div>
                      {/* LOYALTY_TIER_REWARDS_v1 -- einmalige Produktkauf-Rabatte je erreichter Stufe */}
                      {selected.loyaltyTier !== "neu" && (() => {
                        const TIER_SEQ = ["bronze", "silber", "gold", "platin"];
                        const PCT = { bronze: 5, silber: 7, gold: 10, platin: 15 };
                        const LBL = { bronze: "Bronze", silber: "Silber", gold: "Gold", platin: "Platin" };
                        const reachedIdx = TIER_SEQ.indexOf(selected.loyaltyTier);
                        const redeemed = selected.loyaltyRedeemed || [];
                        const isOwner = currentUser && (currentUser.role === "owner" || currentUser.role === "admin");
                        return (
                          <div style={{ marginTop: 8 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }}>Produkt-Rabatte (je Stufe einmalig):</div>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                              {TIER_SEQ.slice(0, reachedIdx + 1).map(t => redeemed.includes(t) ? (
                                <span key={t} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 6, background: "var(--cardHover)", color: COLORS.textLight, border: `1px solid ${COLORS.border}` }}>
                                  ✓ {LBL[t]} {PCT[t]} % eingelöst
                                </span>
                              ) : (
                                <button key={t} disabled={!isOwner || loyaltyBusy}
                                  title={isOwner ? `Einmaligen ${PCT[t]} %-Rabatt auf einen Produktkauf einlösen` : "Nur Inhaber/Admin"}
                                  onClick={async () => {
                                    if (!window.confirm(`${LBL[t]}-Rabatt (${PCT[t]} % auf Produktkauf) jetzt einmalig einlösen?`)) return;
                                    setLoyaltyBusy(true); setLoyaltyErr("");
                                    const res = await api.request(`/customers/${selected.id}/loyalty`, {
                                      method: "POST", body: JSON.stringify({ reward_tier: t }),
                                    });
                                    setLoyaltyBusy(false);
                                    if (res && res.ok && res.data) {
                                      const upd = { loyaltyRedeemed: res.data.redeemed || [] };
                                      setCustomers(prev => prev.map(c => c.id === selected.id ? { ...c, ...upd } : c));
                                      setSelected(s => s ? { ...s, ...upd } : s);
                                    } else {
                                      setLoyaltyErr((res && res.data && res.data.error) || "Einlösen fehlgeschlagen");
                                    }
                                  }}
                                  style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6, border: "none", cursor: isOwner ? "pointer" : "not-allowed", background: COLORS.success, color: "#fff", opacity: isOwner ? 1 : 0.5 }}>
                                  {LBL[t]} {PCT[t]} % einlösen
                                </button>
                              ))}
                            </div>
                            {loyaltyErr && !loyaltyForm && <div style={{ fontSize: 11, color: COLORS.danger, fontWeight: 600, marginTop: 4 }}>{loyaltyErr}</div>}
                          </div>
                        );
                      })()}
                      {loyaltyForm && (
                        <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <input type="number" placeholder="z.B. -50" value={loyaltyForm.points}
                            onChange={e => setLoyaltyForm(f => ({ ...f, points: e.target.value }))}
                            style={{ width: 90, padding: "6px 8px", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 12 }} />
                          <input type="text" placeholder="Begründung (Pflicht)" value={loyaltyForm.note}
                            onChange={e => setLoyaltyForm(f => ({ ...f, note: e.target.value }))}
                            style={{ flex: 1, minWidth: 140, padding: "6px 8px", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 12 }} />
                          <Btn size="sm" disabled={loyaltyBusy} onClick={async () => {
                            setLoyaltyBusy(true); setLoyaltyErr("");
                            const res = await api.request(`/customers/${selected.id}/loyalty`, {
                              method: "POST",
                              body: JSON.stringify({ points: Number(loyaltyForm.points), note: loyaltyForm.note }),
                            });
                            setLoyaltyBusy(false);
                            if (res && res.ok && res.data) {
                              const nb = res.data.balance; const nt = res.data.tier;
                              setCustomers(prev => prev.map(c => c.id === selected.id ? { ...c, loyaltyPoints: nb, loyaltyTier: nt } : c));
                              setSelected(s => s ? { ...s, loyaltyPoints: nb, loyaltyTier: nt } : s);
                              setLoyaltyForm(null);
                            } else {
                              setLoyaltyErr((res && res.data && res.data.error) || "Speichern fehlgeschlagen");
                            }
                          }}>Buchen</Btn>
                          <button onClick={() => setLoyaltyForm(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: COLORS.textLight }}>Abbrechen</button>
                          {loyaltyErr && <span style={{ fontSize: 11, color: COLORS.danger, fontWeight: 600, width: "100%" }}>{loyaltyErr}</span>}
                        </div>
                      )}
                    </div>
                  )}
                  <div><strong>Adresse:</strong> {selected.address || "—"}</div>
                  <div><strong>Notizen:</strong> {selected.notes || "Keine"}</div>
                  {/* CUSTOMER_SMS_TOGGLE_v1 -- direkt im Detail toggelbar (ohne Edit-Modal aufmachen) */}
                  <div style={{ marginTop: 14, padding: "10px 12px", background: "var(--cardHover)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>📱 SMS-Benachrichtigungen</div>
                      <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 2 }}>
                        {selected.smsConsent ? "Kunde bekommt Termin-Bestätigung + Erinnerungen" : "Kein SMS-Versand an diesen Kunden"}
                      </div>
                    </div>
                    <label style={{ position: "relative", display: "inline-block", width: 48, height: 24, cursor: "pointer", flexShrink: 0 }}>
                      <input type="checkbox" checked={!!selected.smsConsent}
                        onChange={async (e) => {
                          const newVal = e.target.checked;
                          // Optimistic
                          setCustomers(prev => prev.map(c => c.id === selected.id ? { ...c, smsConsent: newVal } : c));
                          setSelected(s => s ? { ...s, smsConsent: newVal } : s);
                          const res = await api.updateCustomer(selected.id, { sms_consent: newVal ? 1 : 0 });
                          if (!res || !res.ok) {
                            // Rollback
                            setCustomers(prev => prev.map(c => c.id === selected.id ? { ...c, smsConsent: !newVal } : c));
                            setSelected(s => s ? { ...s, smsConsent: !newVal } : s);
                            alert("Speichern fehlgeschlagen.");
                          }
                        }}
                        style={{ opacity: 0, width: 0, height: 0 }} />
                      <span style={{ position: "absolute", inset: 0, background: selected.smsConsent ? "#10B981" : "#CBD5E1", borderRadius: 12, transition: "background 0.2s" }} />
                      <span style={{ position: "absolute", top: 2, left: selected.smsConsent ? 26 : 2, width: 20, height: 20, background: "white", borderRadius: "50%", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                    </label>
                  </div>
                        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                          {selected.phone && <Btn size="sm" variant="primary" onClick={() => window.open("tel:" + selected.phone)}><Phone size={12} /> Anrufen</Btn>}
                          {selected.phone && <Btn size="sm" variant="secondary" onClick={() => window.open("sms:" + selected.phone)}><MessageSquare size={12} /> SMS</Btn>}
                          {selected.email && <Btn size="sm" variant="secondary" onClick={() => window.open("mailto:" + selected.email)}><Mail size={12} /> E-Mail</Btn>}
            <Btn size="sm" variant="secondary" onClick={() => { setBookingCustomerId(selected.id); setShowBooking(true); }}><Calendar size={12} /> Termin</Btn>
            {showBooking && <BookingModal open={showBooking} onClose={() => { setShowBooking(false); setBookingCustomerId(null); }} employees={employees} customers={customers} services={services} appointments={appointments} setAppointments={setAppointments} preselectCustomerId={bookingCustomerId} />}
                        </div>
                </div>
              )}

              {/* Tab: Hair Type */}
              {crmTab === "hair" && (
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 16 }}>
                    <div style={{ padding: 14, background: "var(--cardHover)", borderRadius: 10 }}>
                      <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 4 }}>Haartyp</div>
                      <div style={{ fontSize: 15, fontWeight: 700 }}>{selected.hairType || "—"}</div>
                    </div>
                    <div style={{ padding: 14, background: "var(--cardHover)", borderRadius: 10 }}>
                      <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 4 }}>Länge</div>
                      <div style={{ fontSize: 15, fontWeight: 700 }}>{selected.hairLength || "—"}</div>
                    </div>
                    <div style={{ padding: 14, background: "var(--cardHover)", borderRadius: 10 }}>
                      <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 4 }}>Stärke</div>
                      <div style={{ fontSize: 15, fontWeight: 700 }}>{selected.hairThickness || "—"}</div>
                    </div>
                    <div style={{ padding: 14, background: "var(--cardHover)", borderRadius: 10 }}>
                      <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 4 }}>Zustand</div>
                      <div style={{ fontSize: 15, fontWeight: 700 }}>{selected.hairCondition || "—"}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.textLight }}>
                    Diese Angaben werden bei der dynamischen Preiskalkulation und bei KI-Telefonanrufen automatisch berücksichtigt.
                  </div>
                </div>
              )}

              {/* Tab: Kundenkartei (Behandlungshistorie) */}
              {crmTab === "card" && (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Neuer Kartei-Eintrag</label>
                    <textarea value={newCardEntry} onChange={e => setNewCardEntry(e.target.value)} placeholder="Behandlung, Rezeptur, Farbmischung, Einwirkzeit, Notizen..."
                      style={{ width: "100%", padding: 10, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, minHeight: 80, resize: "vertical", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} />
                    <div style={{ marginTop: 8 }}>
                      <Btn size="sm" variant="success" onClick={addCardEntry} disabled={!newCardEntry.trim()}><Plus size={12} /> Eintrag speichern</Btn>
                    </div>
                  </div>
                  <div>
                    <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Behandlungs-Historie</h4>
                    {(selected.cardHistory || []).length === 0 ? (
                      <div style={{ padding: 20, textAlign: "center", color: COLORS.textLight, fontSize: 13 }}>Noch keine Einträge</div>
                    ) : (
                      (selected.cardHistory || []).map((entry, i) => (
                        <div key={i} style={{ padding: "12px 14px", marginBottom: 8, background: "var(--cardHover)", borderRadius: 10, borderLeft: `3px solid ${COLORS.primary}` }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 700 }}>{entry.date}</span>
                            <span style={{ fontSize: 11, color: COLORS.textLight }}>{entry.employee}</span>
                          </div>
                          <div style={{ fontSize: 13, lineHeight: 1.6, color: COLORS.text }}>{entry.text}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Tab: Products */}
              {crmTab === "products" && (
                <div>
                  <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Kauft regelmäßig folgende Produkte:</h4>
                  {(selected.regularProducts || []).length === 0 ? (
                    <div style={{ padding: 20, textAlign: "center", color: COLORS.textLight, fontSize: 13 }}>Keine Stammprodukte hinterlegt</div>
                  ) : (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {(selected.regularProducts || []).map((prod, i) => (
                        <div key={i} style={{ padding: "8px 14px", background: `${COLORS.accent}12`, borderRadius: 20, fontSize: 13, fontWeight: 500, color: COLORS.text, border: `1px solid ${COLORS.accent}30` }}>
                          {prod}
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ marginTop: 16, padding: 12, background: `${COLORS.info}08`, borderRadius: 8, fontSize: 12, color: COLORS.textLight }}>
                    Tipp: Produkte hier hinterlegen, damit Mitarbeiter beim nächsten Besuch direkt wissen, was der Kunde braucht oder nachkaufen möchte.
                  </div>
                </div>
              )}

              {/* Tab: Appointment History */}
              {/* CRM_FUTURE_APPTS_v1 -- zukuenftige Termine zuerst (aufsteigend), vergangene darunter (absteigend). Anlass: "Nachname suchen -> kommende Termine sehen". Rein Anzeige, keine Schreibpfade. */}
              {crmTab === "history" && (() => {
                const _todayStr = formatDate(new Date());
                const _all = appointments.filter(a => a.customerId === selected.id);
                const _renderRow = (a) => (
                  <div key={a.id} style={{ padding: "8px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 12, display: "flex", justifyContent: "space-between", alignItems: "center", opacity: a.status === "cancelled" ? 0.55 : 1 }}>
                    <div>
                      <span style={{ fontWeight: 600 }}>{formatDateShort(a.date)} {a.startTime}</span>
                      <span style={{ color: COLORS.textLight, marginLeft: 8 }}>{a.totalDuration} Min.</span>
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <span style={{ fontWeight: 600 }}>{a.totalPrice} €</span>
                      <Badge text={a.status === "confirmed" ? "OK" : a.status === "pending" ? "Offen" : "Abgesagt"} color={a.status === "confirmed" ? COLORS.success : a.status === "pending" ? COLORS.warning : COLORS.danger} />
                      <Badge text={a.paymentStatus === "paid" ? "Bezahlt" : "Offen"} color={a.paymentStatus === "paid" ? COLORS.success : COLORS.textLight} />
                    </div>
                  </div>
                );
                const _upcoming = _all.filter(a => String(a.date || "") >= _todayStr)
                  .sort((x, y) => (x.date + (x.startTime||"")).localeCompare(y.date + (y.startTime||"")));
                const _past = _all.filter(a => String(a.date || "") < _todayStr)
                  .sort((x, y) => (y.date + (y.startTime||"")).localeCompare(x.date + (x.startTime||"")));
                return (
                  <div>
                    {_all.length === 0 ? (
                      <div style={{ padding: 20, textAlign: "center", color: COLORS.textLight, fontSize: 13 }}>Keine Termine gefunden</div>
                    ) : (<>
                      <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: COLORS.primary }}>Kommende Termine ({_upcoming.length})</h4>
                      {_upcoming.length === 0
                        ? <div style={{ padding: "10px 0", color: COLORS.textLight, fontSize: 12 }}>Keine kommenden Termine.</div>
                        : _upcoming.map(_renderRow)}
                      {_past.length > 0 && <>
                        <h4 style={{ fontSize: 13, fontWeight: 700, margin: "18px 0 10px" }}>Vergangene Termine ({_past.length})</h4>
                        {_past.map(_renderRow)}
                      </>}
                    </>)}
                  </div>
                );
              })()}
            </div>
          </Card>
        )}
      </div>

      {/* BULK_CONSENT_v1 -- Einwilligungen fuer alle Kunden setzen */}
      <Modal open={showBulkConsent} onClose={() => !bulkSaving && setShowBulkConsent(false)} title="Einwilligungen für alle Kunden setzen" width={620}>
        {bulkLock && bulkLock.locked && !bulkResult ? (
          <div>
            <div style={{ padding: 16, background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.4)", borderRadius: 8, marginBottom: 16, fontSize: 13, lineHeight: 1.6, color: "var(--text)" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                <CheckCircle size={20} color="#10B981" style={{ flexShrink: 0, marginTop: 2 }} />
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 14 }}>Sammel-Einwilligung bereits bestätigt</div>
                  <div>
                    Du hast die Sammel-Einwilligung am{" "}
                    <strong>{new Date(bulkLock.confirmed_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</strong>{" "}
                    bestätigt.
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12 }}>
                    {bulkLock.set_sms === 1 && <div>✓ SMS-Erinnerungen: alle Kunden auf „Ja" gesetzt</div>}
                    {bulkLock.set_marketing === 1 && <div>✓ Marketing: alle Kunden auf „Ja" gesetzt</div>}
                    {bulkLock.set_sms === 0 && <div>○ SMS-Erinnerungen: alle Kunden auf „Nein" gesetzt</div>}
                    {bulkLock.set_marketing === 0 && <div>○ Marketing: alle Kunden auf „Nein" gesetzt</div>}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ padding: 12, background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 8, marginBottom: 16, fontSize: 12.5, lineHeight: 1.55, color: "var(--text)" }}>
              <strong>Diese Sammel-Aktion kann nicht wiederholt werden.</strong> Das ist ein bewusster Schutz —
              damit kein Mitarbeiter aus Versehen Einwilligungen für alle Kunden zurücksetzt oder mehrfach setzt
              (DSGVO-Beweissicherung).
            </div>
            <div style={{ padding: 12, background: "var(--cardHover)", borderRadius: 8, marginBottom: 16, fontSize: 12.5, lineHeight: 1.6, color: "var(--text)" }}>
              <strong>Was du trotzdem noch tun kannst:</strong>
              <ul style={{ margin: "8px 0 0 18px", padding: 0, color: "var(--textLight)" }}>
                <li>Einzelne Kunden in ihrer Detail-Seite öffnen und Einwilligung individuell ändern (Widerruf-Recht nach DSGVO Art. 7 Abs. 3 — Pflicht!)</li>
                <li>Neue Kunden bekommen Einwilligung beim Erst-Termin oder über das Buchungsformular</li>
                <li>Audit-Log im Admin-Bereich ansehen (Marker BULK_CONSENT_v1)</li>
              </ul>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Btn onClick={() => setShowBulkConsent(false)}>Verstanden</Btn>
            </div>
          </div>
        ) : bulkLock === null ? (
          <div style={{ padding: 30, textAlign: "center", color: "var(--textLight)", fontSize: 13 }}>Lade Status…</div>
        ) : !bulkResult ? (
          <div>
            <div style={{ padding: 12, background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 8, marginBottom: 16, fontSize: 13, lineHeight: 1.55, color: "var(--text)", display: "flex", alignItems: "flex-start", gap: 10 }}>
              <AlertTriangle size={18} color="#F59E0B" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Sammel-Einwilligung — einmalige Aktion</div>
                Du setzt die Einwilligung für <strong>alle {customers.length} Kunden auf einmal</strong>.
                Das geht nur, wenn du die Einwilligung jedes Kunden vorher dokumentiert hast
                (z.B. Treatwell-Übernahme, Papier-Formular, Häkchen beim Erst-Termin).
                <div style={{ marginTop: 8, fontSize: 12, color: "var(--textLight)" }}>
                  Nach Bestätigung wird dieser Button dauerhaft gesperrt. Einzelne Kunden kannst du danach jederzeit
                  in der Detail-Seite ändern.
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", background: "var(--cardHover)", border: `1px solid ${COLORS.border}`, borderRadius: 8, cursor: "pointer", marginBottom: 8 }}>
                <input type="checkbox" checked={bulkSms} onChange={e => setBulkSms(e.target.checked)} style={{ marginTop: 3, accentColor: COLORS.primary }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>SMS-Erinnerungen erlauben</div>
                  <div style={{ fontSize: 11.5, color: "var(--textLight)", marginTop: 2 }}>Termin-Bestätigungen + 24h-Erinnerung. Gilt als Vertragsbestandteil — geringeres Risiko.</div>
                </div>
              </label>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", background: "var(--cardHover)", border: `1px solid ${COLORS.border}`, borderRadius: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={bulkMkt} onChange={e => setBulkMkt(e.target.checked)} style={{ marginTop: 3, accentColor: COLORS.primary }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>Marketing erlauben</div>
                  <div style={{ fontSize: 11.5, color: "var(--textLight)", marginTop: 2 }}>Werbe-Mails/SMS, Geburtstagsglückwünsche, Aktionen. Braucht <strong>echte Einwilligung</strong> — höheres Risiko.</div>
                </div>
              </label>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, color: "var(--text)", lineHeight: 1.5, cursor: "pointer" }}>
                <input type="checkbox" checked={bulkAttest} onChange={e => setBulkAttest(e.target.checked)} style={{ marginTop: 2, accentColor: COLORS.primary }} />
                <span>Ich habe die Einwilligung jedes betroffenen Kunden <strong>dokumentiert</strong> und übernehme dafür die Verantwortung.</span>
              </label>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "var(--textLight)", display: "block", marginBottom: 4 }}>Tippe „JA" zur Bestätigung</label>
              <input value={bulkConfirm} onChange={e => setBulkConfirm(e.target.value)}
                placeholder="JA"
                style={{ width: 120, padding: "8px 12px", border: `1.5px solid ${bulkConfirm.toUpperCase() === "JA" ? COLORS.success : COLORS.border}`, borderRadius: 8, fontSize: 14, fontWeight: 600, textAlign: "center", letterSpacing: 2, background: "var(--card)", color: COLORS.text, outline: "none" }} />
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setShowBulkConsent(false)} disabled={bulkSaving}>Abbrechen</Btn>
              <Btn variant="primary"
                disabled={bulkSaving || !bulkAttest || bulkConfirm.toUpperCase() !== "JA" || (!bulkSms && !bulkMkt)}
                onClick={async () => {
                  setBulkSaving(true);
                  const payload = { confirm_text: "JA" };
                  payload.sms_consent = bulkSms;
                  payload.marketing_consent = bulkMkt;
                  const res = await api.request("/customers/bulk-consent", { method: "POST", body: JSON.stringify(payload) });
                  setBulkSaving(false);
                  if (res.ok) {
                    setBulkResult(res.data);
                    // Reload customers to reflect new state
                    api.getCustomers().then(r => { if (r.ok) setCustomers(r.data || []); });
                  } else {
                    alert("Fehler: " + (res.data?.error || "Unbekannter Fehler"));
                  }
                }}>
                <Check size={14} /> {bulkSaving ? "Speichere…" : 'Auf „Ja" setzen'}
              </Btn>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ padding: 14, background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.4)", borderRadius: 8, marginBottom: 16, fontSize: 13, lineHeight: 1.6, color: "var(--text)", display: "flex", alignItems: "flex-start", gap: 10 }}>
              <CheckCircle size={20} color="#10B981" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Einwilligungen aktualisiert</div>
                <div>Insgesamt <strong>{bulkResult.total_customers}</strong> Kunden im Salon.</div>
                {bulkResult.set_sms !== null && (
                  <div style={{ marginTop: 4 }}>SMS-Erinnerungen: {bulkResult.sms_before} → <strong>{bulkResult.sms_after}</strong> auf „Ja"</div>
                )}
                {bulkResult.set_marketing !== null && (
                  <div>Marketing: {bulkResult.marketing_before} → <strong>{bulkResult.marketing_after}</strong> auf „Ja"</div>
                )}
                <div style={{ marginTop: 8, fontSize: 11.5, color: "var(--textLight)" }}>Aktion ist im Audit-Log dokumentiert (Marker BULK_CONSENT_v1).</div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Btn onClick={() => { setShowBulkConsent(false); setBulkResult(null); }}>Schließen</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* New Customer Modal - erweitert */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Neuen Kunden anlegen" width={600}>
        <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: "0 12px" }}>
          <Input label="Vor- und Nachname *" value={newCust.name} onChange={e => setNewCust(n => ({ ...n, name: e.target.value }))} />
          <Input label="Telefon *" value={newCust.phone} onChange={e => setNewCust(n => ({ ...n, phone: e.target.value }))} />
          <Input label="E-Mail" value={newCust.email} onChange={e => setNewCust(n => ({ ...n, email: e.target.value }))} />
          <Input label="Adresse" value={newCust.address} onChange={e => setNewCust(n => ({ ...n, address: e.target.value }))} />
          <BirthdayField value={newCust.birthday} onChange={v => setNewCust(n => ({ ...n, birthday: v }))} />
        </div>
        {/* CUST_DUP_HINT_v1 -- Hinweis auf bestehende Kunden mit gleicher Nummer / gleichem Namen (keine Sperre) */}
        <DupCustomerHint hits={showAdd ? findDuplicateCustomers(customers, { name: newCust.name, phone: newCust.phone }) : []}
          pickLabel="Kundenkarte öffnen"
          onPick={c => { setShowAdd(false); setSearch(""); setFilterType("all"); setSelected(c); }} />
        <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" /* NEWCUST_MOBILE_FIT_v1 -- SMS-Einwilligung war am Handy abgeschnitten */ }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Kundentyp</label>
            <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
              {["stammkunde", "laufkunde"].map(t => (
                <button key={t} onClick={() => setNewCust(n => ({ ...n, customerType: t }))} style={{
                  padding: "6px 14px", borderRadius: 20, fontSize: 12, cursor: "pointer",
                  background: newCust.customerType === t ? `${COLORS.primary}15` : "#f3f4f6",
                  color: newCust.customerType === t ? COLORS.primary : COLORS.textLight,
                  border: `1px solid ${newCust.customerType === t ? COLORS.primary : COLORS.border}`, fontWeight: newCust.customerType === t ? 600 : 400
                }}>{t === "stammkunde" ? "Stammkunde" : "Laufkunde"}</button>
              ))}
            </div>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Kommt regelmäßig</label>
            <div style={{ marginTop: 6 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                <input type="checkbox" checked={newCust.comesRegularly} onChange={e => setNewCust(n => ({ ...n, comesRegularly: e.target.checked }))} /> Ja, kommt regelmäßig
              </label>
            </div>
          </div>
                  <div style={{ marginTop: 8 }}>
                    <label style={{ fontSize: 13, fontWeight: 600 }}>SMS-Einwilligung</label>
                    <div style={{ marginTop: 6 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                        <input type="checkbox" checked={newCust.smsConsent} onChange={e => setNewCust(n => ({ ...n, smsConsent: e.target.checked }))} /> Kunde stimmt SMS-Benachrichtigungen zu
                      </label>
                    </div>
                  </div>
        </div>
        <div style={{ padding: 12, background: "var(--cardHover)", borderRadius: 8, marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Haartyp (optional)</label>
          <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: "0 12px" }}>
            <Select label="Typ" value={newCust.hairType} onChange={e => setNewCust(n => ({ ...n, hairType: e.target.value }))} options={[{ value: "", label: "—" }, ...HAIR_TYPES.map(v => ({ value: v, label: v }))]} />
            <Select label="Länge" value={newCust.hairLength} onChange={e => setNewCust(n => ({ ...n, hairLength: e.target.value }))} options={[{ value: "", label: "—" }, ...HAIR_LENGTHS.map(v => ({ value: v, label: v }))]} />
            <Select label="Stärke" value={newCust.hairThickness} onChange={e => setNewCust(n => ({ ...n, hairThickness: e.target.value }))} options={[{ value: "", label: "—" }, ...HAIR_THICKNESS.map(v => ({ value: v, label: v }))]} />
            <Select label="Zustand" value={newCust.hairCondition} onChange={e => setNewCust(n => ({ ...n, hairCondition: e.target.value }))} options={[{ value: "", label: "—" }, ...HAIR_CONDITIONS.map(v => ({ value: v, label: v }))]} />
          </div>
        </div>
        <Input label="Stammprodukte (kommagetrennt)" placeholder="z.B. Olaplex No.3, Kerastase Elixir" onChange={e => setNewCust(n => ({ ...n, regularProducts: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }))} />
        <Input label="Notizen" value={newCust.notes} onChange={e => setNewCust(n => ({ ...n, notes: e.target.value }))} />
            {/* AI_HEALTH_HINT_v1 / Compliance B10 */}
            <div style={{ fontSize: 10.5, color: "var(--textLight)", marginTop: -6, marginBottom: 10, lineHeight: 1.35 }}>🔒 Gesundheitsangaben (z. B. Allergien) nur mit ausdrücklicher Einwilligung des Kunden eintragen.</div>
        {/* NEW_CUST_QUICK_BOOK_v1 -- 2 Buttons: nur speichern ODER speichern+gleich Termin */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
          <Btn variant="success" onClick={() => addCustomer(false)} disabled={!newCust.name || !newCust.phone}>
            <Check size={14} /> Kunde anlegen
          </Btn>
          <Btn variant="primary" onClick={() => addCustomer(true)} disabled={!newCust.name || !newCust.phone}
               title="Speichert den Kunden und öffnet direkt die Termin-Buchung mit diesem Kunden vorausgewählt">
            <Calendar size={14} /> Anlegen & Termin vergeben
          </Btn>
        </div>
      </Modal>

      {/* CUSTOMER_EDIT_v1 -- Bearbeiten-Modal fuer bestehende Kundendaten */}
      <Modal open={editCust !== null} onClose={() => setEditCust(null)} title="Kunde bearbeiten" width={600}>
        {editCust && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: "0 12px", marginBottom: 14 }}>
              <Input label="Vor- und Nachname *" value={editCust.name} onChange={e => setEditCust(n => ({ ...n, name: e.target.value }))} />
              <Input label="Telefon *" value={editCust.phone} onChange={e => setEditCust(n => ({ ...n, phone: e.target.value }))} />
              <Input label="E-Mail" value={editCust.email} onChange={e => setEditCust(n => ({ ...n, email: e.target.value }))} />
              <BirthdayField value={editCust.birthday} onChange={v => setEditCust(n => ({ ...n, birthday: v }))} />
              <Select label="Geschlecht" value={editCust.gender} onChange={e => setEditCust(n => ({ ...n, gender: e.target.value }))} options={[{ value: "", label: "—" }, { value: "M", label: "Männlich" }, { value: "F", label: "Weiblich" }, { value: "K", label: "Kind" }]} />
              <Select label="Lieblings-Mitarbeiter" value={editCust.preferredEmployee} onChange={e => setEditCust(n => ({ ...n, preferredEmployee: e.target.value }))} options={[{ value: "", label: "—" }, ...employees.map(e => ({ value: e.id, label: e.name }))]} />
            </div>

            <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600 }}>Kundentyp</label>
                <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                  {["stammkunde", "laufkunde"].map(t => (
                    <button key={t} onClick={() => setEditCust(n => ({ ...n, customerType: t }))} style={{
                      padding: "6px 14px", borderRadius: 20, fontSize: 12, cursor: "pointer",
                      background: editCust.customerType === t ? `${COLORS.primary}15` : "var(--cardHover)",
                      color: editCust.customerType === t ? COLORS.primary : COLORS.textLight,
                      border: `1px solid ${editCust.customerType === t ? COLORS.primary : COLORS.border}`, fontWeight: editCust.customerType === t ? 600 : 400
                    }}>{t === "stammkunde" ? "Stammkunde" : "Laufkunde"}</button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600 }}>Kommt regelmäßig</label>
                <div style={{ marginTop: 6 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                    <input type="checkbox" checked={!!editCust.comesRegularly} onChange={e => setEditCust(n => ({ ...n, comesRegularly: e.target.checked }))} /> Ja
                  </label>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600 }}>SMS-Einwilligung</label>
                <div style={{ marginTop: 6 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                    <input type="checkbox" checked={!!editCust.smsConsent} onChange={e => setEditCust(n => ({ ...n, smsConsent: e.target.checked }))} /> SMS erlaubt
                  </label>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600 }}>Marketing-Einwilligung</label>
                <div style={{ marginTop: 6 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                    <input type="checkbox" checked={!!editCust.marketingConsent} onChange={e => setEditCust(n => ({ ...n, marketingConsent: e.target.checked }))} /> Marketing erlaubt
                  </label>
                </div>
              </div>
            </div>

            <div style={{ padding: 12, background: "var(--cardHover)", borderRadius: 8, marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Haartyp</label>
              <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: "0 12px" }}>
                <Select label="Typ" value={editCust.hairType} onChange={e => setEditCust(n => ({ ...n, hairType: e.target.value }))} options={[{ value: "", label: "—" }, ...HAIR_TYPES.map(v => ({ value: v, label: v }))]} />
                <Select label="Länge" value={editCust.hairLength} onChange={e => setEditCust(n => ({ ...n, hairLength: e.target.value }))} options={[{ value: "", label: "—" }, ...HAIR_LENGTHS.map(v => ({ value: v, label: v }))]} />
                <Select label="Stärke" value={editCust.hairThickness} onChange={e => setEditCust(n => ({ ...n, hairThickness: e.target.value }))} options={[{ value: "", label: "—" }, ...HAIR_THICKNESS.map(v => ({ value: v, label: v }))]} />
                <Select label="Zustand" value={editCust.hairCondition} onChange={e => setEditCust(n => ({ ...n, hairCondition: e.target.value }))} options={[{ value: "", label: "—" }, ...HAIR_CONDITIONS.map(v => ({ value: v, label: v }))]} />
              </div>
            </div>

            {/* CUSTOMER_ADDRESS_v1 -- eigenes Adressfeld */}
            <Input label="Adresse" value={editCust.address} onChange={e => setEditCust(n => ({ ...n, address: e.target.value }))} />
            <Input label="Notizen" value={editCust.notes} onChange={e => setEditCust(n => ({ ...n, notes: e.target.value }))} />
            {/* AI_HEALTH_HINT_v1 / Compliance B10 */}
            <div style={{ fontSize: 10.5, color: "var(--textLight)", marginTop: -6, marginBottom: 10, lineHeight: 1.35 }}>🔒 Gesundheitsangaben (z. B. Allergien) nur mit ausdrücklicher Einwilligung des Kunden eintragen.</div>

            <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setEditCust(null)}>Abbrechen</Btn>
              <Btn variant="success" disabled={!editCust.name || !editCust.phone} onClick={async () => {
                const payload = {
                  name: editCust.name,
                  phone: editCust.phone,
                  email: editCust.email,
                  birthday: editCust.birthday || null,
                  gender: editCust.gender,
                  notes: editCust.notes,
                  address: editCust.address,
                  hair_type: editCust.hairType,
                  hair_length: editCust.hairLength,
                  hair_thickness: editCust.hairThickness,
                  hair_condition: editCust.hairCondition,
                  customer_type: editCust.customerType,
                  comes_regularly: !!editCust.comesRegularly,
                  preferred_employee_id: editCust.preferredEmployee || null,
                  sms_consent: !!editCust.smsConsent,
                  marketing_consent: !!editCust.marketingConsent,
                };
                const res = await api.updateCustomer(selected.id, payload);
                if (res && res.ok) {
                  // Local state update -- frontend Felder camelCase, Backend snake_case
                  const merged = {
                    ...selected,
                    name: payload.name,
                    phone: payload.phone,
                    email: payload.email,
                    birthday: payload.birthday,
                    gender: payload.gender,
                    notes: payload.notes,
                    address: payload.address,
                    hairType: payload.hair_type,
                    hairLength: payload.hair_length,
                    hairThickness: payload.hair_thickness,
                    hairCondition: payload.hair_condition,
                    customerType: payload.customer_type,
                    comesRegularly: payload.comes_regularly,
                    preferredEmployee: payload.preferred_employee_id,
                    smsConsent: payload.sms_consent,
                    marketingConsent: payload.marketing_consent,
                  };
                  setCustomers(prev => prev.map(c => c.id === selected.id ? merged : c));
                  setSelected(merged);
                  setEditCust(null);
                } else {
                  // CRM_EDIT_ERROR_DETAIL_v1 -- Backend-Error zeigen statt generic
                  const detail = (res && res.data && res.data.error) || (res && res.status ? "HTTP " + res.status : "Unbekannt");
                  alert("Speichern fehlgeschlagen:\n\n" + detail);
                  console.error("Customer update failed:", res);
                }
              }}><Check size={14} /> Speichern</Btn>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};